import React, { Component, ErrorInfo, ReactNode } from 'react';

/**
 * Props interface for ErrorBoundary component
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

/**
 * State interface for ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree and displays
 * a fallback UI instead of crashing the entire application.
 * 
 * Features:
 * - Graceful error handling with user-friendly messages
 * - Error reporting and logging
 * - Reset functionality to recover from errors
 * - Customizable fallback UI
 * - Development vs production error displays
 * 
 * This component provides production-ready error handling for the FillyOnIris workflow.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  /**
   * Static method called when an error occurs
   * Updates state to trigger error UI
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.error('🚨 ERROR BOUNDARY: Error caught by boundary:', error);
    
    return {
      hasError: true,
      error,
      eventId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  /**
   * Called after an error has been thrown by a descendant component
   * Used for error reporting and logging
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 ERROR BOUNDARY: Component stack trace:', errorInfo.componentStack);
    console.error('🚨 ERROR BOUNDARY: Full error details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    // Update state with error info
    this.setState({
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (handlerError) {
        console.error('🚨 ERROR BOUNDARY: Error in custom error handler:', handlerError);
      }
    }

    // In production, you might want to send this to an error reporting service
    // Example: Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      this.reportErrorToService(error, errorInfo);
    }
  }

  /**
   * Check if component should reset based on prop changes
   */
  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state if resetKeys have changed
    if (hasError && resetOnPropsChange && resetKeys) {
      const hasResetKeyChanged = resetKeys.some((key, index) => 
        prevProps.resetKeys?.[index] !== key
      );

      if (hasResetKeyChanged) {
        console.log('🔄 ERROR BOUNDARY: Resetting due to prop changes');
        this.resetErrorBoundary();
      }
    }
  }

  /**
   * Cleanup on unmount
   */
  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  /**
   * Reports error to external service (placeholder for production)
   */
  private reportErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Placeholder for error reporting service integration
    // In a real application, you would send this to Sentry, LogRocket, etc.
    console.log('📊 ERROR REPORTING: Would send to error service:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });
  };

  /**
   * Resets the error boundary state
   */
  private resetErrorBoundary = () => {
    console.log('🔄 ERROR BOUNDARY: Resetting error state');
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
  };

  /**
   * Handles retry with delay to prevent rapid retries
   */
  private handleRetry = () => {
    console.log('🔄 ERROR BOUNDARY: User initiated retry');
    
    // Add a small delay to prevent rapid clicking
    this.resetTimeoutId = window.setTimeout(() => {
      this.resetErrorBoundary();
    }, 500);
  };

  /**
   * Renders the error UI or children
   */
  render() {
    const { hasError, error, errorInfo, eventId } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="error-boundary-container" style={{
          padding: '2rem',
          margin: '1rem',
          border: '1px solid #ff6b6b',
          borderRadius: '8px',
          backgroundColor: '#fff5f5',
          color: '#c92a2a',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div className="error-boundary-content">
            {/* Error Icon */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <svg 
                width="48" 
                height="48" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                style={{ color: '#ff6b6b' }}
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>

            {/* Error Title */}
            <h2 style={{ 
              margin: '0 0 1rem 0', 
              fontSize: '1.5rem', 
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Something went wrong
            </h2>

            {/* Error Description */}
            <p style={{ 
              margin: '0 0 1.5rem 0', 
              fontSize: '1rem', 
              lineHeight: '1.5',
              textAlign: 'center',
              color: '#666'
            }}>
              We encountered an unexpected error. Don't worry - your data is safe and we're working to fix this.
            </p>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && error && (
              <details style={{ 
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: '600' }}>
                  Error Details (Development)
                </summary>
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>Error:</strong> {error.message}
                  {error.stack && (
                    <pre style={{ 
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '2px',
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {error.stack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleRetry}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#0056b3';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#007bff';
                }}
              >
                Try Again
              </button>

              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#545b62';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#6c757d';
                }}
              >
                Refresh Page
              </button>
            </div>

            {/* Error ID for Support */}
            {eventId && (
              <p style={{ 
                marginTop: '1.5rem',
                fontSize: '0.875rem',
                color: '#6c757d',
                textAlign: 'center'
              }}>
                Error ID: <code style={{ 
                  backgroundColor: '#f8f9fa',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '2px',
                  fontFamily: 'monospace'
                }}>{eventId}</code>
              </p>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
